import axios from "axios";
import {getCookie} from "@/utils/general-utils";
import { store } from "@/store";

// Purge any stale localStorage flag from the earlier (unsafe) implementation so
// nobody stays stuck impersonating after loading this build. Act-as is now an
// in-memory, Studio-only flag (store.studioActingAsReader) that resets every load.
try { localStorage.removeItem("actingAsReader"); } catch (e) { /* ignore */ }

// Ensure cookies are sent with every request (needed for CSRF)
axios.defaults.withCredentials = true;

// Set default headers for all requests
axios.defaults.headers.common["Content-Type"] = "application/json";

// No request may outlive the server's own 120s worker timeout: a request that
// never settles is a spinner that never stops. Long lanes (image jobs) set
// their own, longer timeout per call.
axios.defaults.timeout = 120000;

// A stale token used to leave the app looking signed in (get-querent is keyed
// by querent_id, not the token) while every authenticated call quietly 401'd.
// Now: drop the dead credentials once and tell the page, which reopens the
// sign-in gate (ReaderPage listens for 'ma:auth-expired').
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error && error.response && error.response.status;
    const sentToken = !!(error && error.config && error.config.headers &&
      (error.config.headers.Authorization || error.config.headers.authorization));
    if (status === 401 && sentToken) {
      try {
        localStorage.removeItem("authToken");
        localStorage.removeItem("querent_id");
        localStorage.removeItem("qid");
      } catch (e) { /* storage unavailable */ }
      store.querent_id = "";
      try { window.dispatchEvent(new CustomEvent("ma:auth-expired")); } catch (e) { /* no window */ }
    }
    return Promise.reject(error);
  }
);

// Automatically add CSRF token to all requests
axios.interceptors.request.use((config) => {
  const csrfToken = getCookie("csrftoken"); // Read CSRF token from cookies
  if (csrfToken) {
    config.headers["X-CSRFToken"] = csrfToken; // Attach CSRF token
  }
  // Superuser "act as reader" — Studio ONLY. Sent only while the in-memory flag
  // is set (i.e. actively editing another reader inside the Studio), and cleared
  // the moment you leave the Studio or refresh. Honored server-side only for
  // superusers. This scoping is what keeps impersonation out of Altar/Quest.
  if (store.studioActingAsReader) {
    config.headers["X-Act-As-Reader"] = store.studioActingAsReader;
  }
  return config;
});

export default axios; 
